/**
 * Карта v3 — рендер на Canvas 2D.
 *
 * Один проход по сцене рисует всё; порядок слоёв фиксирован, как на чертеже:
 * сетка → рамки локаций → трассы → подписи трасс → корпуса устройств → оверлей.
 * Корпуса выше трасс намеренно: линия, уходящая под блок, читается нормально,
 * а линия, перечёркивающая иконку, читается как ошибка.
 */
import { THEME, DASH, CORNER, HOP, LOD, NODE_R, CONT_R, PAD_TOP } from './const';
import { near, segCross, pointAtFraction } from './math';
import { portsOfNode } from './ports';
import { getSprite, spriteUrl } from './sprites';

/** Полилиния со скруглёнными углами — рисуем как дуги на стыках сегментов. */
function tracePath(ctx, pts, radius) {
  if (pts.length < 4) return;
  ctx.moveTo(pts[0], pts[1]);
  for (let i = 2; i + 1 < pts.length; i += 2) {
    const x = pts[i], y = pts[i + 1];
    if (i + 3 >= pts.length) { ctx.lineTo(x, y); break; }
    const nx = pts[i + 2], ny = pts[i + 3];
    const px = pts[i - 2], py = pts[i - 1];

    const d1 = Math.hypot(x - px, y - py);
    const d2 = Math.hypot(nx - x, ny - y);
    const r = Math.min(radius, d1 / 2, d2 / 2);
    if (r < 1.5) { ctx.lineTo(x, y); continue; }

    const t1x = x - ((x - px) / (d1 || 1)) * r;
    const t1y = y - ((y - py) / (d1 || 1)) * r;
    const t2x = x + ((nx - x) / (d2 || 1)) * r;
    const t2y = y + ((ny - y) / (d2 || 1)) * r;

    ctx.lineTo(t1x, t1y);
    ctx.quadraticCurveTo(x, y, t2x, t2y);
  }
}

/**
 * Размер шрифта в МИРОВЫХ единицах, но так, чтобы на экране он никогда не
 * оказался мельче minPx. Внутри ctx.scale(z,z) любой кегль умножается на зум,
 * и на 30 % имена превращаются в трёхпиксельную кашу — ровно та беда, за
 * которую забракована карта v1. Здесь текст растёт при отдалении и остаётся
 * читаемым, а лишнее просто не влезает и обрезается многоточием.
 */
function fontWorld(base, z, minPx = 9) {
  return Math.max(base, minPx / (z || 1));
}

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function hexToRgba(hex, a) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || '').trim());
  if (!m) return `rgba(100,116,139,${a})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${a})`;
}

/** Простые векторные пиктограммы по типу устройства — без внешних ресурсов. */
function drawIcon(ctx, kind, x, y, size, color) {
  const s = size;
  ctx.save();
  ctx.translate(x, y);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  switch (kind) {
    case 'switch':
      ctx.rect(-s / 2, -s / 4, s, s / 2);
      ctx.moveTo(-s / 4, -s / 4); ctx.lineTo(-s / 4, s / 4);
      ctx.moveTo(0, -s / 4); ctx.lineTo(0, s / 4);
      ctx.moveTo(s / 4, -s / 4); ctx.lineTo(s / 4, s / 4);
      break;
    case 'router':
      ctx.arc(0, 0, s / 2.4, 0, Math.PI * 2);
      ctx.moveTo(-s / 4, 0); ctx.lineTo(s / 4, 0);
      ctx.moveTo(s / 4, 0); ctx.lineTo(s / 8, -s / 8);
      ctx.moveTo(s / 4, 0); ctx.lineTo(s / 8, s / 8);
      break;
    case 'server':
      ctx.rect(-s / 2.6, -s / 2.2, s / 1.3, s / 1.1);
      ctx.moveTo(-s / 3, -s / 5); ctx.lineTo(s / 3, -s / 5);
      ctx.moveTo(-s / 3, 0); ctx.lineTo(s / 3, 0);
      ctx.moveTo(-s / 3, s / 5); ctx.lineTo(s / 3, s / 5);
      break;
    case 'pc':
      ctx.rect(-s / 2, -s / 2.6, s, s / 1.7);
      ctx.moveTo(-s / 4, s / 2.4); ctx.lineTo(s / 4, s / 2.4);
      ctx.moveTo(0, s / 3.4); ctx.lineTo(0, s / 2.4);
      break;
    case 'ups':
      ctx.rect(-s / 2.6, -s / 2.2, s / 1.3, s / 1.1);
      ctx.moveTo(s / 12, -s / 4); ctx.lineTo(-s / 8, s / 20); ctx.lineTo(s / 24, s / 20);
      ctx.lineTo(-s / 12, s / 4);
      break;
    case 'plc':
      ctx.rect(-s / 2.2, -s / 2.6, s / 1.1, s / 1.3);
      ctx.moveTo(-s / 2.2, -s / 6); ctx.lineTo(s / 2.2, -s / 6);
      ctx.moveTo(-s / 2.2, s / 6); ctx.lineTo(s / 2.2, s / 6);
      break;
    case 'wifi':
      ctx.arc(0, s / 4, 2, 0, Math.PI * 2);
      ctx.moveTo(-s / 5, s / 12); ctx.arc(0, s / 4, s / 4, Math.PI * 1.25, Math.PI * 1.75);
      ctx.moveTo(-s / 2.6, -s / 8); ctx.arc(0, s / 4, s / 2, Math.PI * 1.2, Math.PI * 1.8);
      break;
    case 'camera':
      ctx.rect(-s / 2, -s / 5, s / 1.5, s / 2.6);
      ctx.moveTo(s / 6, -s / 12); ctx.lineTo(s / 2, -s / 4); ctx.lineTo(s / 2, s / 5);
      ctx.lineTo(s / 6, s / 24);
      break;
    case 'printer':
      ctx.rect(-s / 2.2, -s / 8, s / 1.1, s / 2.6);
      ctx.rect(-s / 3.4, -s / 2.4, s / 1.7, s / 4);
      break;
    default:
      ctx.rect(-s / 2.4, -s / 2.4, s / 1.2, s / 1.2);
  }
  ctx.stroke();
  ctx.restore();
}

const ICON_RULES = [
  [/коммутатор|switch|sw/i, 'switch'],
  [/маршрутизатор|router|шлюз|gateway|fw|межсет/i, 'router'],
  [/сервер|server|nvr/i, 'server'],
  [/пк|компьютер|рабочая станция|pc|моноблок|ноут/i, 'pc'],
  [/ибп|ups|pdu|питание/i, 'ups'],
  [/плк|plc|контроллер|панель оператора|hmi|частотн|vfd/i, 'plc'],
  [/wi-?fi|точка доступа|ap\b/i, 'wifi'],
  [/камер|camera|cam/i, 'camera'],
  [/принтер|мфу|printer/i, 'printer'],
];

export function iconKind(typeName, deviceName) {
  const hay = `${typeName || ''} ${deviceName || ''}`;
  for (const [re, kind] of ICON_RULES) if (re.test(hay)) return kind;
  return 'box';
}

/**
 * Главный проход рендера.
 * @param ctx    2d-контекст (уже с применённым devicePixelRatio)
 * @param s      сцена
 * @param vp     вьюпорт {x,y,scale}
 * @param view   размеры канваса в CSS-пикселях
 * @param ui     состояние интерфейса: выделение, наведение, тема, флаги
 */
export function render(ctx, s, vp, view, ui) {
  const T = THEME[ui.theme === 'dark' ? 'dark' : 'light'];
  const z = vp.scale;

  ctx.save();
  ctx.fillStyle = T.bg;
  ctx.fillRect(0, 0, view.w, view.h);

  if (ui.grid) drawGrid(ctx, vp, view, T);

  ctx.translate(vp.x, vp.y);
  ctx.scale(z, z);

  // видимая область в мировых координатах — всё за её пределами не рисуем
  const vis = {
    x: -vp.x / z - 100, y: -vp.y / z - 100,
    w: view.w / z + 200, h: view.h / z + 200,
  };
  const visible = (r) =>
    r.x < vis.x + vis.w && r.x + r.w > vis.x && r.y < vis.y + vis.h && r.y + r.h > vis.y;

  // ---- рамки локаций ----
  for (const id of s.order) {
    const c = s.conts.get(id);
    if (!c || !visible(c)) continue;
    const selected = ui.selection.has(c.id);
    ctx.fillStyle = hexToRgba(c.fill, Math.max(0.05, (c.opacity || 18) / 100));
    ctx.strokeStyle = selected ? T.sel : c.border;
    ctx.lineWidth = (selected ? 2.4 : 1.4) / z;
    roundRect(ctx, c.x, c.y, c.w, c.h, CONT_R);
    ctx.fill();
    ctx.stroke();

    if (z > LOD.DOT) {
      ctx.fillStyle = T.contText;
      ctx.font = `600 ${fontWorld(14, z, 11).toFixed(1)}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      const label = c.name.length > 46 ? `${c.name.slice(0, 44)}…` : c.name;
      ctx.fillText(label, c.x + 14, c.y + PAD_TOP / 2 + 2);
    }
  }

  // ---- трассы ----
  const edges = [...s.edges.values()].filter((e) => e.route && e.route.pts.length >= 4);
  const dim = ui.filterActive;

  // мостики на пересечениях: считаем только на крупном масштабе
  const hops = z >= LOD.FULL && ui.hops !== false ? computeHops(edges) : new Map();

  for (const e of edges) {
    const sel = ui.selection.has(e.id);
    const hot = ui.hover && ui.hover.type === 'edge' && ui.hover.id === e.id;
    const muted = dim && !ui.filtered.has(e.id);

    ctx.strokeStyle = muted ? hexToRgba(e.color, 0.16)
      : (e.approx ? hexToRgba(e.color, 0.5) : e.color);
    ctx.lineWidth = Math.max(1.1, (sel ? e.width + 1.6 : e.width)) / (z < 1 ? 1 : 1);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.setLineDash((DASH[e.style] || []).map((v) => v));

    if (sel || hot) {
      ctx.save();
      ctx.strokeStyle = T.selSoft;
      ctx.lineWidth = e.width + 9;
      ctx.setLineDash([]);
      ctx.beginPath();
      tracePath(ctx, e.route.pts, CORNER);
      ctx.stroke();
      ctx.restore();
    }

    ctx.beginPath();
    if (z >= LOD.FULL && hops.has(e.id)) drawWithHops(ctx, e.route.pts, hops.get(e.id));
    else tracePath(ctx, e.route.pts, CORNER);
    ctx.stroke();
    ctx.setLineDash([]);

    if (z > LOD.PLAIN && e.direction && e.direction !== 'bidirectional') {
      drawArrow(ctx, e.route.pts, e.color, muted);
    }
  }

  // ---- подписи трасс ----
  // Каждая подпись ищет себе свободное место вдоль своей линии. Если места нет,
  // подпись прячется: три одинаковых плашки «Оптика (Fiber)» в одном углу
  // сообщают ровно столько же, сколько одна, но мешают читать схему.
  if (ui.labels !== 'none' && z > LOD.PLAIN) {
    const lf = fontWorld(11, z, 9);
    ctx.font = `500 ${lf.toFixed(1)}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    const placed = [];
    const FRACTIONS = [0.5, 0.36, 0.64, 0.24, 0.76];
    const overlaps = (a) => placed.some((b) =>
      a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h);

    // длинные трассы подписываем первыми — у них больше шансов найти место,
    // и именно они обычно и есть магистрали, которые надо подписать
    const ordered = edges
      .filter((e) => !e.labelCfg?.hidden && !(dim && !ui.filtered.has(e.id)) && labelText(e, ui.labels))
      .map((e) => ({ e, len: e.route.pts.length }))
      .sort((p, q) => q.len - p.len);

    for (const { e } of ordered) {
      const text = labelText(e, ui.labels);
      const wpx = ctx.measureText(text).width;
      const bw = wpx + lf, bh = lf * 1.7;
      const pad = 3 / z;

      let box = null;
      for (const f of FRACTIONS) {
        const p = pointAtFraction(e.route.pts, f);
        const px = p.x + (e.labelCfg?.dx || 0);
        const py = p.y + (e.labelCfg?.dy || 0);
        const cand = { x: px - bw / 2 - pad, y: py - bh / 2 - pad, w: bw + pad * 2, h: bh + pad * 2, px, py };
        if (!overlaps(cand)) { box = cand; break; }
      }
      if (!box) continue;                       // свободного места нет — молчим
      placed.push(box);

      ctx.fillStyle = T.labelBg;
      ctx.strokeStyle = T.labelBorder;
      ctx.lineWidth = 1 / z;
      roundRect(ctx, box.px - wpx / 2 - lf * 0.45, box.py - bh / 2, wpx + lf * 0.9, bh, lf * 0.4);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = T.labelText;
      ctx.fillText(text, box.px, box.py + 0.5);
    }
  }

  // ---- устройства ----
  for (const n of s.nodes.values()) {
    if (n.hidden || !visible(n)) continue;
    const sel = ui.selection.has(n.id);
    const hot = ui.hover && ui.hover.type === 'node' && ui.hover.id === n.id;
    const muted = dim && !ui.filtered.has(n.id);

    if (z < LOD.DOT) {
      ctx.fillStyle = muted ? hexToRgba(T.sel, 0.2) : T.sel;
      ctx.fillRect(n.x + n.w / 2 - 3, n.y + n.h / 2 - 3, 6, 6);
      continue;
    }

    ctx.save();
    ctx.globalAlpha = muted ? 0.28 : 1;
    if (!muted) {
      ctx.shadowColor = T.shadow;
      ctx.shadowBlur = 6 / z;
      ctx.shadowOffsetY = 1 / z;
    }
    ctx.fillStyle = T.node;
    ctx.strokeStyle = sel || hot ? T.sel : T.nodeBorder;
    ctx.lineWidth = (sel ? 2.4 : 1.2) / z;
    roundRect(ctx, n.x, n.y, n.w, n.h, NODE_R);
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.stroke();
    ctx.restore();

    if (muted) continue;

    // Картинка устройства, если она загружена и показ включён; иначе — векторная
    // иконка по типу. Картинка вписывается в квадрат с сохранением пропорций,
    // чтобы вытянутые фото не растягивало.
    let iconW = 0;
    if (z > LOD.PLAIN) {
      const url = ui.images !== false ? spriteUrl(n) : null;
      const img = url ? getSprite(url) : null;
      if (img) {
        const box = Math.min(n.h - 14, 34);
        const k = Math.min(box / img.naturalWidth, box / img.naturalHeight);
        const iw = img.naturalWidth * k, ih = img.naturalHeight * k;
        ctx.save();
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, n.x + 8 + (box - iw) / 2, n.y + (n.h - ih) / 2, iw, ih);
        ctx.restore();
        iconW = box + 8;
      } else {
        drawIcon(ctx, iconKind(n.typeName, n.name), n.x + 20, n.y + n.h / 2 - 6, 20, T.sel);
        iconW = 28;
      }
    }

    ctx.fillStyle = T.nodeText;
    const nf = fontWorld(12, z, 9);
    ctx.font = `600 ${nf.toFixed(1)}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    const textX = n.x + 8 + iconW;
    const name = fit(ctx, n.name, n.x + n.w - 8 - textX);
    ctx.fillText(name, textX, n.y + n.h / 2 - nf * 0.12);

    if (z > LOD.PLAIN) {
      ctx.fillStyle = T.nodeSub;
      const sf = fontWorld(10, z, 8);
      ctx.font = `400 ${sf.toFixed(1)}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
      const raw = z > LOD.FULL && n.ip ? n.ip : (n.typeName || '');
      ctx.fillText(fit(ctx, raw, n.x + n.w - 8 - textX), textX, n.y + n.h / 2 + nf * 1.05);
    }

    // засечки портов — видно, что к устройству подходит несколько кабелей
    if (ui.showPorts && z > LOD.PLAIN) {
      const list = portsOfNode(s, n.id);
      for (const p of list) {
        ctx.fillStyle = p.edge.color;
        ctx.beginPath();
        ctx.arc(p.pt.x, p.pt.y, 2.6, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  ctx.restore();
}

function labelText(e, mode) {
  if (mode === 'type') return e.label || '';
  if (mode === 'iface') return [e.aIface, e.bIface].filter(Boolean).join(' → ');
  if (mode === 'subnet') return e.subnet || e.vlan || '';
  return '';
}

function fit(ctx, text, maxW) {
  if (!text) return '';
  if (ctx.measureText(text).width <= maxW) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxW) t = t.slice(0, -1);
  return `${t}…`;
}

/** Стрелка направления в конце трассы. */
function drawArrow(ctx, pts, color, muted) {
  const n = pts.length;
  const x2 = pts[n - 2], y2 = pts[n - 1];
  const x1 = pts[n - 4], y1 = pts[n - 3];
  const a = Math.atan2(y2 - y1, x2 - x1);
  ctx.save();
  ctx.setLineDash([]);
  ctx.fillStyle = muted ? 'rgba(100,116,139,0.2)' : color;
  ctx.translate(x2, y2);
  ctx.rotate(a);
  ctx.beginPath();
  ctx.moveTo(0, 0); ctx.lineTo(-9, -4); ctx.lineTo(-9, 4);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/**
 * Точки, где трассы пересекаются: линия, идущая «сверху», рисуется с мостиком,
 * и становится видно, что кабели не соединяются, а просто пересекаются.
 */
function computeHops(edges) {
  const map = new Map();
  for (let a = 0; a < edges.length; a++) {
    for (let b = a + 1; b < edges.length; b++) {
      const A = edges[a].route.pts, B = edges[b].route.pts;
      for (let i = 0; i + 3 < A.length; i += 2) {
        for (let j = 0; j + 3 < B.length; j += 2) {
          const c = segCross(A[i], A[i + 1], A[i + 2], A[i + 3], B[j], B[j + 1], B[j + 2], B[j + 3]);
          if (!c) continue;
          const key = edges[a].id;
          if (!map.has(key)) map.set(key, []);
          map.get(key).push({ seg: i / 2, x: c.x, y: c.y });
        }
      }
    }
  }
  return map;
}

function drawWithHops(ctx, pts, hops) {
  const bySeg = new Map();
  for (const h of hops) {
    if (!bySeg.has(h.seg)) bySeg.set(h.seg, []);
    bySeg.get(h.seg).push(h);
  }
  ctx.moveTo(pts[0], pts[1]);
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const x1 = pts[i], y1 = pts[i + 1], x2 = pts[i + 2], y2 = pts[i + 3];
    const list = (bySeg.get(i / 2) || []).slice();
    const horiz = near(y1, y2, 0.6);
    list.sort((p, q) => (horiz ? (x2 > x1 ? p.x - q.x : q.x - p.x) : (y2 > y1 ? p.y - q.y : q.y - p.y)));
    for (const h of list) {
      if (horiz) {
        const dir = x2 > x1 ? 1 : -1;
        ctx.lineTo(h.x - dir * HOP, y1);
        ctx.arc(h.x, y1, HOP, dir > 0 ? Math.PI : 0, dir > 0 ? 0 : Math.PI, false);
      } else {
        const dir = y2 > y1 ? 1 : -1;
        ctx.lineTo(x1, h.y - dir * HOP);
        ctx.arc(x1, h.y, HOP, dir > 0 ? -Math.PI / 2 : Math.PI / 2, dir > 0 ? Math.PI / 2 : -Math.PI / 2, dir < 0);
      }
    }
    ctx.lineTo(x2, y2);
  }
}

function drawGrid(ctx, vp, view, T) {
  const z = vp.scale;
  let step = 24;
  while (step * z < 14) step *= 4;
  while (step * z > 90) step /= 2;
  const x0 = Math.floor(-vp.x / z / step) * step;
  const y0 = Math.floor(-vp.y / z / step) * step;
  const cols = Math.ceil(view.w / (step * z)) + 2;
  const rows = Math.ceil(view.h / (step * z)) + 2;

  ctx.save();
  ctx.lineWidth = 1;
  for (let i = 0; i < cols; i++) {
    const wx = x0 + i * step;
    const sx = Math.round(wx * z + vp.x) + 0.5;
    ctx.strokeStyle = wx % (step * 5) === 0 ? T.gridStrong : T.grid;
    ctx.beginPath(); ctx.moveTo(sx, 0); ctx.lineTo(sx, view.h); ctx.stroke();
  }
  for (let j = 0; j < rows; j++) {
    const wy = y0 + j * step;
    const sy = Math.round(wy * z + vp.y) + 0.5;
    ctx.strokeStyle = wy % (step * 5) === 0 ? T.gridStrong : T.grid;
    ctx.beginPath(); ctx.moveTo(0, sy); ctx.lineTo(view.w, sy); ctx.stroke();
  }
  ctx.restore();
}

/** Оверлей поверх сцены: ручки изгибов, порты выделенной связи, рамка выделения. */
export function renderOverlay(ctx, s, vp, view, ui) {
  const T = THEME[ui.theme === 'dark' ? 'dark' : 'light'];
  ctx.clearRect(0, 0, view.w, view.h);
  ctx.save();
  ctx.translate(vp.x, vp.y);
  ctx.scale(vp.scale, vp.scale);
  const inv = 1 / vp.scale;

  // ручки изгибов у выделенных связей
  for (const id of ui.selection) {
    const e = s.edges.get(id);
    if (!e || !e.route) continue;

    // существующие изгибы — сплошные ручки
    (e.vias || []).forEach((v, i) => {
      const p = ui.viaPoints?.get(`${e.id}:${i}`) || { x: v.x, y: v.y };
      ctx.fillStyle = T.handle;
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2 * inv;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 7 * inv, 0, Math.PI * 2);
      ctx.fill(); ctx.stroke();
    });

    // подсказка «здесь можно взяться»: середины сегментов помечены точками,
    // но тянуть на самом деле можно за любое место линии
    const pts = e.route.pts;
    for (let i = 0; i + 3 < pts.length; i += 2) {
      const mx = (pts[i] + pts[i + 2]) / 2, my = (pts[i + 1] + pts[i + 3]) / 2;
      const len = Math.abs(pts[i + 2] - pts[i]) + Math.abs(pts[i + 3] - pts[i + 1]);
      if (len < 30) continue;
      ctx.fillStyle = hexToRgba(T.handle, 0.75);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.4 * inv;
      ctx.beginPath();
      ctx.arc(mx, my, 5 * inv, 0, Math.PI * 2);
      ctx.fill(); ctx.stroke();
    }

    // порты этой связи
    for (const [pt, side] of [[e.aPt, e.aSide], [e.bPt, e.bSide]]) {
      if (!pt) continue;
      ctx.strokeStyle = T.sel;
      ctx.fillStyle = '#fff';
      ctx.lineWidth = 2 * inv;
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, 4.5 * inv, 0, Math.PI * 2);
      ctx.fill(); ctx.stroke();
    }
  }

  // уголки изменения размера у выделенной локации
  for (const id of ui.selection) {
    const c = s.conts.get(id);
    if (!c) continue;
    const r = 4.5 * inv;
    ctx.fillStyle = '#fff';
    ctx.strokeStyle = T.sel;
    ctx.lineWidth = 2 * inv;
    for (const [gx, gy] of [[c.x, c.y], [c.x + c.w, c.y], [c.x, c.y + c.h], [c.x + c.w, c.y + c.h]]) {
      ctx.beginPath();
      ctx.rect(gx - r, gy - r, r * 2, r * 2);
      ctx.fill(); ctx.stroke();
    }
  }

  // направляющие выравнивания
  if (ui.guides) {
    ctx.save();
    ctx.strokeStyle = T.guide;
    ctx.lineWidth = 1 * inv;
    ctx.setLineDash([6 * inv, 4 * inv]);
    for (const g of ui.guides) {
      ctx.beginPath();
      if (g.axis === 'x') { ctx.moveTo(g.v, g.from); ctx.lineTo(g.v, g.to); }
      else { ctx.moveTo(g.from, g.v); ctx.lineTo(g.to, g.v); }
      ctx.stroke();
    }
    ctx.restore();
  }

  ctx.restore();

  // рамка выделения рисуется в экранных координатах
  if (ui.marquee) {
    const m = ui.marquee;
    ctx.save();
    ctx.fillStyle = T.selSoft;
    ctx.strokeStyle = T.sel;
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 3]);
    ctx.fillRect(m.x, m.y, m.w, m.h);
    ctx.strokeRect(m.x, m.y, m.w, m.h);
    ctx.restore();
  }
}
